/**
 * Pricing engine for configurable glass.
 *
 * Pure and dependency-free: the server re-runs these exact functions against
 * trusted Firestore data when an order is placed (the authority), and the client
 * may run them on public rates for a live indication. Client input is never
 * trusted for the final amount.
 *
 * All returned amounts are integer eurocents, excl. BTW unless the field says total.
 */

import type { Cents } from "../types/money";
import { type BewerkingsTarieven } from "./bewerkingstarieven";
import type { ConfigInput, ProcessingRequest } from "../types/config";
import type { EdgeFinish, GlassVariant, Shape } from "../types/product";
import type { PriceResult, ProcessingLine } from "../types/pricing";
import { DEFAULT_SPELING_MM, billableArea, resolveNetDimensions } from "./dimensions";
import { validateGlassConfig, formatValidationIssue } from "./validate";
import {
  RONDE_HOEK_MODEL_MM,
  energySurcharge,
  isModelRoundedCorner,
  modelRoundedCornerCount,
  glasModelPct,
  rondeHoekModelPct,
  sizeSurcharge,
} from "./surcharges";
import { edgeCost, perimeterM, selectedSidesLengthM } from "./edge";
import { EXTRA_FOLIE_CENTS, edgeCostFromWorksheet, edgeWorksheetParts, glasModelPctBijSlijpen, isFacetAfwerking, facetRondeHoekCents, isSlijpAfwerking, laminatedOpCents, slijpModelMinimumCents, type GrindFamily } from "./grinding";
import { heeftInbegrepenPP, metGratisSparingen, resolveProcessingLines } from "./processings";
import { addVat } from "./vat";
import { PricingError } from "./errors";

/** Resolved context for a price calculation (looked up from IDs by the caller). */
export interface PriceContext {
  variant: GlassVariant;
  shape?: Shape;
  edgeFinish?: EdgeFinish;
  /** Total clearance subtracted per dimension for sponningmaat (mm). */
  spelingMm?: number;
  /** Pre-computed length of edges to finish (mm) — for per-edge selection on any shape. */
  grindLengthMm?: number;
  /**
   * Toeslag (%) op het SLIJPWERK voor niet-rechtlijnige modellen (schuine zijde,
   * cirkel, ovaal en afgeronde hoek >250mm). 0/undefined = uit (standaard).
   * Achter een schakelaar tot Timmers de exacte regel bevestigt. (Prijslijst p11.)
   */
  edgeShapeSurchargePct?: number;
  /** Glasfamilie voor de slijptabellen ("enkel" als niets is meegegeven). */
  grindFamily?: GrindFamily;
  /** Dikte in mm (enkel) of opbouw ((gehard-)gelaagd) — de sleutel in de slijptabel. */
  grindKey?: number | string;
  /**
   * Marge-factor voor bewerkingen (slijpwerk + boringen/uitsparingen/hoeken). De slijptabellen
   * zijn netto INKOOP; bewerkingen volgen dezelfde marge als het glas. 1 = geen marge.
   */
  opsMarginFactor?: number;
  /** Inkooptarieven voor bewerkingen (admin-instelling). Weglaten = de code-standaard. */
  opsTarieven?: BewerkingsTarieven;
  /**
   * Productslug — nodig voor regels die per soort gelden, zoals de vijf uitsparingen die bij
   * installatieglas al in de m²-prijs zitten (zie `metGratisSparingen`).
   */
  productSlug?: string;
}

/** Modellen waarvoor het slijpwerk de niet-rechtlijnige toeslag krijgt (Prijslijst p11). */
export const EDGE_SHAPE_SURCHARGE_IDS = new Set(["schuine-zijde", "schuine-zijden-2", "achthoek", "cirkel", "ovaal"]);

const toCents = (value: number): Cents => Math.round(value);

/**
 * Calculates the full price breakdown for a configured glass pane.
 * @throws {PricingError} when the configuration is invalid.
 */
export function calculateGlassPrice(
  input: ConfigInput,
  ctx: PriceContext,
): PriceResult {
  const { variant, shape, edgeFinish } = ctx;
  const spelingMm = ctx.spelingMm ?? DEFAULT_SPELING_MM;

  const { netWidthMm, netHeightMm } = resolveNetDimensions(input, spelingMm);

  const issues = validateGlassConfig({
    netWidthMm,
    netHeightMm,
    quantity: input.quantity,
    variant,
    edgeFinish,
    processings: input.processings,
    grindFamily: ctx.grindFamily,
    shapeId: shape?.id,
  });
  if (issues.length > 0) throw new PricingError(issues.map(formatValidationIssue));

  const areaM2 = (netWidthMm / 1000) * (netHeightMm / 1000);
  const billableAreaM2 = Math.max(
    billableArea(netWidthMm, netHeightMm),
    variant.minAreaM2,
  );

  const perimeter = perimeterM(netWidthMm, netHeightMm, shape);
  const edgeLengthM =
    ctx.grindLengthMm != null
      ? ctx.grindLengthMm / 1000
      : input.selectedSides && input.selectedSides.length > 0
        ? selectedSidesLengthM(netWidthMm, netHeightMm, input.selectedSides)
        : perimeter;

  // Timmers rounds EACH line to whole cents, then sums them — verified against the
  // live webshop (Floatglas 1000×1000 and 2000×1800, 2026-06-14). The unit price is
  // that sum; quantity multiplies the rounded unit price; BTW is added on the total.
  const baseGlass = billableAreaM2 * variant.pricePerM2;
  // Afronding > 250 mm is bij Timmers een MODEL, geen bewerking: een paneeltoeslag over het glas
  // waarvan het percentage met het AANTAL grote afrondingen meeloopt (1–2 → +80%, 3–4 → +120%).
  // Gehard-gelaagd valt hierbuiten — daar krijgt élke ronde hoek al zijn eigen paneeltoeslag van
  // +120% (gehardGelaagdToeslagPct), en dat dubbel rekenen mag niet.
  const grindFamily = ctx.grindFamily ?? "enkel";
  const modelCorners =
    grindFamily === "gehard-gelaagd" ? [] : (input.processings ?? []).filter(isModelRoundedCorner);
  const rondeHoekPct = rondeHoekModelPct(modelRoundedCornerCount(modelCorners));
  const rondeHoekModelRaw = baseGlass * (rondeHoekPct / 100);
  // Modeltoeslag op het GLAS — de vorm zelf plus (bij PP/facet) het slijpen van dat model.
  // Zie glasModelPct voor de bronnen en de gehard-gelaagd-uitzondering.
  const ggHeeftRondeHoek =
    grindFamily === "gehard-gelaagd" && (input.processings ?? []).some((p) => p.kind === "hoek");
  const vormPct = glasModelPct({ shape, family: grindFamily, heeftRondeHoek: ggHeeftRondeHoek });
  // "Modeltoeslag ivm slijpen": een model dat geslepen wordt maakt óók het glas duurder, en die
  // toeslag wordt VERMENIGVULDIGD met de vormtoeslag — glasprijs × (1 + slijpmodel) × (1 + vorm).
  // Bron: rekenblad "Berekening PP slijpen" (Thijs, 29-07-2026); een cirkel van € 59,59/m² komt
  // daarmee op 59,59 × 2 × 2,2 = € 262,20/m².
  const modelSlijpPct =
    edgeLengthM > 0 && isSlijpAfwerking(edgeFinish?.id) ? glasModelPctBijSlijpen(grindFamily, shape?.id) : 0;
  const glasNaModel = baseGlass * (1 + vormPct / 100) * (1 + modelSlijpPct / 100);
  const modelPct = baseGlass > 0 ? (glasNaModel / baseGlass - 1) * 100 : vormPct;
  const shapeSurchargeRaw = glasNaModel - baseGlass + rondeHoekModelRaw;
  // Het deel dat bij de vórm hoort en het deel dat er bij komt omdat dat model geslepen wordt —
  // op de bon horen dat twee regels te zijn.
  const shapeSlijpRaw = glasNaModel - baseGlass * (1 + vormPct / 100);

  // Grootte-toeslag op het glas: de oppervlakte-staffel uit de catalogus (+50% >3 m², +100% >6 m²),
  // gerekend over glas + modeltoeslagen.
  //
  // Deze toeslag stond even uit, omdat het rekenblad "Berekening PP slijpen" hem alleen naast het
  // SLIJPWERK noemt. Daarna bleek de prijs niet meer overeen te komen met de Timmers-webshop: die
  // rekent hem óók op het glas (live gecontroleerd op 14-06-2026, 2000×1800 → "Grootte toeslag
  // 50%"). Hij hoort er dus bij; het rekenblad is op dit punt onvolledig. (Thijs, 29-07-2026.)
  //
  // (Gehard-)gelaagd blijft uitgezonderd: daar zit de meerprijs voor een grote plaat al in de
  // opbouw-tarieven van de prijslijst (Thijs, 28-07-2026).
  const glassRaw = baseGlass + shapeSurchargeRaw;
  const grootGlasToeslag = grindFamily !== "gelaagd" && grindFamily !== "gehard-gelaagd";
  const size = grootGlasToeslag
    ? sizeSurcharge(glassRaw, areaM2, variant.sizeSurcharges)
    : { amount: 0, pct: 0 };
  const energy = energySurcharge(areaM2, variant);

  // Slijpwerk: eerst de werkbladen (PP/facet, netto inkoop met oppervlaktetrap, minimum-m1 en
  // vormtoeslag). Staat de afwerking daar niet in (verstek, C-slijp), dan de catalogusprijs.
  const werkblad = edgeCostFromWorksheet(edgeFinish?.id, {
    family: grindFamily,
    key: ctx.grindKey ?? variant.thicknessMm,
    thicknessMm: variant.thicknessMm,
    lengthM: edgeLengthM,
    areaM2,
    shapeId: shape?.id,
  });
  // Installatieglas komt rondom PP-geslepen uit de fabriek en dat zit in de m²-prijs — daar mag
  // dus geen slijptarief overheen (zie heeftInbegrepenPP).
  const ppInbegrepen = heeftInbegrepenPP(ctx.productSlug);
  let edgeRaw = ppInbegrepen ? 0 : werkblad != null ? werkblad : edgeCost(edgeLengthM, edgeFinish);
  const uitWerkblad = ppInbegrepen || werkblad != null;
  // Opbouw van het slijpwerk voor de Controle. Elke stap die `edgeRaw` verandert zet hier zijn
  // eigen regel neer, zodat de opbouw ALTIJD optelt tot wat er gerekend wordt — ook als een
  // minimumtarief of een catalogusprijs het overneemt.
  const edgeSteps: { label: string; amount: number; pct?: number; meters?: number; perMeter?: number }[] = [];
  const edgeStap = (label: string, amount: number, extra?: { pct?: number; meters?: number; perMeter?: number }): void => {
    if (Math.abs(amount) >= 0.5) edgeSteps.push({ label, amount, ...extra });
  };
  // Minimaal te berekenen slijpwerk: 1 strekkende meter (rechthoek, via edge.minPrice),
  // maar 2 strekkende meter bij MODELLEN (niet-rechthoekige vormen). (Prijslijst p11.)
  // NB: de client geeft óók voor rechthoek een shape-object mee → expliciet uitsluiten,
  // anders krijgt een rechthoek ten onrechte het 2m-model-minimum.
  if (!uitWerkblad && edgeFinish && shape && shape.id !== "rechthoek" && edgeLengthM > 0) {
    const modelMin = edgeFinish.pricePerMeter * 2;
    if (edgeRaw < modelMin) {
      edgeStap("Minimum 2 m1 bij een model", modelMin - edgeRaw);
      edgeRaw = modelMin;
    }
  }
  const edgeParts = ppInbegrepen
    ? null
    : edgeWorksheetParts(edgeFinish?.id, {
        family: grindFamily,
        key: ctx.grindKey ?? variant.thicknessMm,
        lengthM: edgeLengthM,
        areaM2,
        shapeId: shape?.id,
      });
  if (edgeRaw > 0) {
    if (edgeParts && edgeParts.total === edgeRaw) {
      // PP/afscherpen uit het werkblad: tarief → afmetingtoeslag → vormtoeslag.
      edgeStap("Randafwerking", edgeParts.base, { meters: edgeParts.meters, perMeter: edgeParts.perMeter });
      edgeStap("Afmetingtoeslag slijpwerk", edgeParts.sizeAmount, { pct: edgeParts.sizePct });
      edgeStap("Vormtoeslag slijpwerk", edgeParts.shapeAmount, { pct: edgeParts.shapePct });
    } else {
      // Facet, verstek, C-slijp of de catalogusprijs: geen tabel om uit te splitsen.
      edgeStap("Randafwerking", edgeRaw, edgeFinish ? { meters: edgeLengthM, perMeter: edgeFinish.pricePerMeter } : undefined);
    }
  }
  // Gefactureerde slijplengte in strekkende meter (ná het minimum van 1 m1 rechthoek /
  // 2 m1 bij modellen), vóór de vorm-toeslag% — puur informatief voor de Controle.
  const edgeLengthBilledM = edgeFinish && edgeRaw > 0 ? edgeRaw / edgeFinish.pricePerMeter : 0;
  // Slijpwerk-toeslag voor niet-rechtlijnige modellen (achter een schakelaar): +X%
  // op het slijpwerk bij schuine zijde / cirkel / ovaal / afgeronde hoek >250mm.
  const edgeShapePct = uitWerkblad ? 0 : ctx.edgeShapeSurchargePct ?? 0;
  if (edgeShapePct > 0 && edgeRaw > 0) {
    const shapeQualifies = shape != null && EDGE_SHAPE_SURCHARGE_IDS.has(shape.id);
    const bigRoundedCorner = (input.processings ?? []).some((p) => p.kind === "hoek" && p.sizeMm > 250);
    if (shapeQualifies || bigRoundedCorner) {
      const toeslag = edgeRaw * (edgeShapePct / 100);
      edgeRaw += toeslag;
      edgeStap("Vormtoeslag slijpwerk", toeslag, { pct: edgeShapePct });
    }
  }
  // Minimum slijploon bij een MODEL: elke vorm behalve een rechthoek kost minstens € 30 aan
  // slijpwerk (gelaagd € 40), zodra er écht geslepen wordt (PP of facet). Een kleine cirkel kwam
  // anders op een paar euro slijploon uit terwijl de machine er net zo lang over doet.
  // Facet kent géén minimumbedrag: daar is het minimum 1 strekkende meter slijpen (2 m1 bij een
  // model), en dat zit al in het facettarief. (Thijs, 28-07-2026.)
  if (!ppInbegrepen && isSlijpAfwerking(edgeFinish?.id) && !isFacetAfwerking(edgeFinish?.id) && edgeLengthM > 0) {
    const modelMinimum = slijpModelMinimumCents(grindFamily, shape?.id);
    if (edgeRaw < modelMinimum) {
      edgeStap("Minimumtarief slijpwerk model", modelMinimum - edgeRaw);
      edgeRaw = modelMinimum;
    }
  }

  // Gehard-gelaagd rekent ronde hoeken als PANEELtoeslag (zit hierboven al in `modelPct`), niet
  // per stuk. De losse hoek-regels vervallen dan, anders reken je het dubbel.

  // Een afronding met een DOORLOPEND FACET heeft een eigen stukprijs uit het facet-werkblad
  // (€37,50 / €45 / €52,50 per dikte). Die vervangt de gewone hoekstaffel — het is één bewerking,
  // geen hoek mét een toeslag. Alleen ronde hoeken t/m 250 mm; daarboven is het een model.
  const facetRand = edgeFinish?.facetMm != null;
  const isFacetHoek = (p: ProcessingRequest): boolean =>
    facetRand && p.kind === "hoek" && (p.hoekType ?? "rond") === "rond" && p.sizeMm <= RONDE_HOEK_MODEL_MM;
  const facetCornerUnit = facetRand ? facetRondeHoekCents(variant.thicknessMm) : null;

  // Hoeken die al als paneeltoeslag zijn gerekend krijgen géén stukprijs meer: bij gehard-gelaagd
  // álle ronde hoeken (+120%), daarbuiten de afrondingen > 250 mm (+80%). Voor die laatste bestaat
  // sowieso geen staffel — resolveProcessingLines zou er een "niet mogelijk"-fout op geven.
  // De facet-hoeken gaan er ook uit; die krijgen hieronder hun eigen regel.
  const alleOps = input.processings ?? [];
  const perStuk = ggHeeftRondeHoek
    ? alleOps.filter((p) => p.kind !== "hoek")
    : alleOps.filter((p) => !isModelRoundedCorner(p) && !(facetCornerUnit != null && isFacetHoek(p)));

  // Gelaagd glas: +25% op boringen, uitsparingen en hoeken (werkblad "PP slijpen uitgelegd WEL
  // gelaagd": "Deze tabel (pag 13/14) aanhouden + 25% voor gelaagdglas"). De catalogus levert
  // alleen de gewone tarieven, dus zonder deze opslag rekende gelaagd de enkelglas-prijs.
  // Per stuk verhogen en dán vermenigvuldigen — zo staat de stukprijs op de bon ook goed.
  // Installatieglas: de eerste vijf sparingen zitten al in de m²-prijs (Timmers GLASS_DOORS).
  const teRekenen = metGratisSparingen(perStuk, ctx.productSlug);
  const processings = resolveProcessingLines(teRekenen, variant, grindFamily, ctx.opsTarieven).map((p): ProcessingLine => {
    const unit = p.unitCost != null ? laminatedOpCents(toCents(p.unitCost), grindFamily) : undefined;
    return {
      name: p.name,
      label: p.label,
      count: p.count,
      unitCost: unit,
      cost: unit != null && p.count != null ? toCents(unit * p.count) : laminatedOpCents(toCents(p.cost), grindFamily),
    };
  });

  // De afrondingen met doorlopend facet als één eigen regel. Kent het werkblad geen prijs voor
  // deze dikte (19 mm), dan is `facetCornerUnit` null en zijn de hoeken hierboven NIET uit de
  // staffel gehaald — die combinatie hoort dan via uncertainCombination op aanvraag te gaan.
  const facetCorners = facetCornerUnit != null ? alleOps.filter(isFacetHoek).reduce((n, p) => n + p.count, 0) : 0;
  if (facetCorners > 0 && facetCornerUnit != null) {
    processings.push({
      name: `Ronde hoek met doorlopend facet × ${facetCorners}`,
      label: "Ronde hoek met doorlopend facet",
      count: facetCorners,
      unitCost: toCents(facetCornerUnit),
      cost: toCents(facetCornerUnit * facetCorners),
    });
  }

  // Extra folie (gehard-gelaagd): vaste toeslag per ruit voor één laag van 0,38 mm. Het bedrag uit
  // het werkblad is netto — geen inkoopkorting eroverheen, alleen de gewone marge.
  const folieKeuze = grindFamily === "gehard-gelaagd" ? input.extraFolie : undefined;
  const extraFolieRaw = folieKeuze ? EXTRA_FOLIE_CENTS[folieKeuze] : 0;

  // One rounded line each, exactly as the Timmers price breakdown shows them.
  const glassPrice = toCents(baseGlass);
  const shapeSurchargeCents = toCents(shapeSurchargeRaw);
  const sizeSurchargeCents = toCents(size.amount);
  const energySurchargeCents = toCents(energy.amount);
  // Bewerkingen zijn netto inkoop → dezelfde marge als het glas eroverheen.
  const opsFactor = ctx.opsMarginFactor && ctx.opsMarginFactor > 0 ? ctx.opsMarginFactor : 1;
  const edgePrice = toCents(edgeRaw * opsFactor);
  // De opbouw gaat door dezelfde marge als het slijpwerk zelf. De laatste regel vangt het
  // afrondingsverschil op, zodat de regels ALTIJD optellen tot het bedrag dat gerekend wordt.
  const edgeLines = (() => {
    if (!edgeSteps.length || edgePrice <= 0) return undefined;
    const rijen = edgeSteps.map((st) => ({
      label: st.label,
      ...(st.pct != null ? { pct: st.pct } : {}),
      ...(st.meters != null ? { meters: Math.round(st.meters * 100) / 100 } : {}),
      ...(st.perMeter != null ? { perMeterCents: toCents(st.perMeter * opsFactor) } : {}),
      amountCents: toCents(st.amount * opsFactor),
    }));
    const som = rijen.reduce((n, r) => n + r.amountCents, 0);
    const laatste = rijen[rijen.length - 1]!;
    laatste.amountCents = (laatste.amountCents + (edgePrice - som)) as Cents;
    return rijen;
  })();

  const extraFolieCents = toCents(extraFolieRaw * opsFactor);
  const processingsCost = Math.round(processings.reduce((sum, p) => sum + p.cost, 0) * opsFactor);
  // Elke bewerkingsregel krijgt zijn eigen verkoopbedrag mét marge. Zonder dit toonde de Controle
  // de netto inkoopprijs van een boring of uitsparing, terwijl het totaal wél marge bevatte.
  // De laatste regel vangt het afrondingsverschil op, zodat de regels optellen tot het totaal.
  const verkoopRegels = processings.map((p) => ({ ...p, sellCost: toCents(p.cost * opsFactor) }));
  if (verkoopRegels.length > 0) {
    const som = verkoopRegels.reduce((n, p) => n + (p.sellCost ?? 0), 0);
    const laatste = verkoopRegels[verkoopRegels.length - 1]!;
    laatste.sellCost = (laatste.sellCost + (processingsCost - som)) as Cents;
  }

  const unitPrice =
    glassPrice +
    shapeSurchargeCents +
    sizeSurchargeCents +
    energySurchargeCents +
    extraFolieCents +
    edgePrice +
    processingsCost;
  const subtotalExcl = unitPrice * input.quantity;
  const { vat, total } = addVat(subtotalExcl);

  return {
    netWidthMm,
    netHeightMm,
    areaM2,
    billableAreaM2,
    perimeterM: perimeter,
    glassPrice,
    shapeSurcharge: shapeSurchargeCents,
    shapeSurchargePct: modelPct,
    shapeSurchargeSlijp: toCents(shapeSlijpRaw),
    shapeSurchargeSlijpPct: modelSlijpPct,
    extraFolie: extraFolieCents,
    sizeSurcharge: sizeSurchargeCents,
    sizeSurchargePct: size.pct,
    energySurcharge: energySurchargeCents,
    weightKg: energy.weightKg,
    edgePrice,
    edgeLengthBilledM,
    edgeNetCost: toCents(edgeRaw),
    // Alleen meesturen als de uitsplitsing ook echt optelt tot wat er gerekend is: zodra een
    // minimumtarief of de catalogusprijs het overneemt, klopt de opbouw niet meer.
    ...(edgeLines ? { edgeLines } : {}),
    processingsCost,
    processings: verkoopRegels,
    // Het paneelminimum hoort óók in de getoonde stuksprijs terug te komen, anders telt de
    // regel niet op met het subtotaal.
    unitPrice,
    quantity: input.quantity,
    subtotalExcl,
    vat,
    totalIncl: total,
  };
}

/** Aggregates line subtotals (excl. BTW, in cents) into order totals. */
export function calculateOrderTotals(lineSubtotals: Cents[]): {
  subtotal: Cents;
  vat: Cents;
  total: Cents;
} {
  const subtotal = lineSubtotals.reduce((sum, line) => sum + line, 0);
  const { vat, total } = addVat(subtotal);
  return { subtotal, vat, total };
}
